"use client";

import { useEffect, useRef } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { useProducts } from "@/modules/products/hooks/use-products";
import { useCurrencyStore, useRegionStore } from "@/store/index";
import { ProductsGrid } from "@/modules/products/components/products-grid";
import { Pagination } from "@/modules/common/components/pagination";
import { Skeleton } from "@/modules/common/components/skeleton";

const PAGE_SIZE = 24;

export const HomeCatalog = ({ countryCode }: { countryCode: string }) => {
  const searchParams = useSearchParams();
  const { currencyCode } = useCurrencyStore();
  const { regionId } = useRegionStore();

  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const categoryId = searchParams.get("category_id");

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    document
      .getElementById("catalogo")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [page]);

  const { data, isLoading } = useProducts({
    countryCode,
    regionId: regionId || undefined,
    currencyCode,
    queryParams: {
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      ...(categoryId && { category_id: [categoryId] }),
    },
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6 xl:grid-cols-4">
        {[...Array(PAGE_SIZE)].map((_, i) => (
          <Skeleton key={i} className="h-60 w-full" />
        ))}
      </div>
    );
  }

  const products = data?.products ?? [];
  const count = data?.count ?? 0;

  if (products.length === 0) {
    return (
      <div className="py-10 text-center">
        <p className="font-inter mb-4 text-gray-500">
          No hay productos en esta categoría todavía.
        </p>
        {categoryId && (
          <Link
            href={`/${countryCode}`}
            className="text-green-main font-medium underline"
          >
            Ver todos los productos
          </Link>
        )}
      </div>
    );
  }

  const start = (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, count);

  return (
    <>
      <p className="font-inter mb-4 text-sm text-gray-600">
        Mostrando {start}–{end} de {count} productos
      </p>
      <ProductsGrid products={products} />
      <Pagination
        totalItems={count}
        itemsPerPage={PAGE_SIZE}
        currentPage={page}
      />
    </>
  );
};
